import React from 'react';
import { globalMessages } from 'components/shared/lang';
import { Form, Modal } from 'react-bootstrap';
import { Button } from '../ui';
import { Form as FinalForm, Field } from 'react-final-form';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';

// Id je převzaté z legacy katalogu beze změny.
const messages = defineMessages({
    updateType: { id: 'aip.form.update.type', defaultMessage: 'Co se má s AIPem udělat' },
});
import { AipDetailVO, AipProblemType, AipUpdateType } from "elza-api";

import { updateTypeDescriptions, updateTypeMessages, updateTypeUnavailable } from './messages';
import './AipUpdateTypeForm.scss';

interface FormFields {
    type: AipUpdateType;
}

interface Props {
    initialValues?: FormFields;
    /** AIPy, kterých se akce týká; podle jejich stavu se nabízejí jen použitelné volby. */
    aips?: AipDetailVO[];
    onSubmit: (values: FormFields) => void;
    onClose?: () => void;
}

/**
 * Proč volbu nelze použít, nebo null, když použít lze.
 *
 * Volba, která by u žádného z vybraných AIPů nic neudělala, se nenabízí - jinak
 * uživatel stiskne tlačítko a nic se nestane, aniž by se dozvěděl proč.
 */
const unavailableReason = (type: AipUpdateType, aips: AipDetailVO[]) => {
    if (aips.length === 0) {
        return null;
    }
    const some = (predicate: (aip: AipDetailVO) => boolean) => aips.some(predicate);
    switch (type) {
        case AipUpdateType.DbUpdate:
        case AipUpdateType.ForceUpdate:
            // sestavení z uloženého balíčku potřebuje stažená metadata
            return some(aip => aip.metadataLoad === true) ? null : updateTypeUnavailable.needsMetadata;
        case AipUpdateType.RemapReferences:
            return some(aip => aip.problemType === AipProblemType.UnknownFund
                    || aip.problemType === AipProblemType.UnknownInstitution)
                ? null : updateTypeUnavailable.needsUnresolved;
        default:
            return null;
    }
};

/**
 * Pořadí od nejčastější volby. Vynucená aktualizace se nenabízí: sestavení z uloženého balíčku
 * napojení na části, které balíček už nemá, uzavře samo, takže by dělala totéž.
 */
const UPDATE_TYPES: AipUpdateType[] = [
    AipUpdateType.RemapReferences,
    AipUpdateType.DownloadUpdate,
    AipUpdateType.DbUpdate,
];

export function AipUpdateTypeForm({
    initialValues,
    aips = [],
    onSubmit,
    onClose
}: Props) {
    const intl = useIntl();

    function validate(values: FormFields) {
        const errors: Partial<Record<keyof FormFields, string>> = {};

        if (!values.type) {
            errors.type = intl.formatMessage(globalMessages.validationRequired);
        }

        return errors;
    }

    async function handleSubmit(values: FormFields) {
        await onSubmit(values);
        onClose();
    }

    return (
        <FinalForm<FormFields>
            initialValues={initialValues}
            validate={validate}
            onSubmit={handleSubmit}
        >{({ submitting, handleSubmit }) => {
            return <Form className="aip-update-type">
                <Modal.Body>
                    <Field name="type">
                        {({input, meta}) => (
                            <fieldset>
                                <legend className="form-label"><FormattedMessage {...messages.updateType} /></legend>
                                {UPDATE_TYPES.map(type => {
                                    const unavailable = unavailableReason(type, aips);
                                    return (
                                        <Form.Check
                                            key={type}
                                            type="radio"
                                            id={`aipUpdateType-${type}`}
                                            name={input.name}
                                            value={type}
                                            checked={input.value === type}
                                            onChange={() => input.onChange(type)}
                                            disabled={submitting || unavailable != null}
                                            label={
                                                <>
                                                    <span className="update-type-name">
                                                        {intl.formatMessage(updateTypeMessages[type])}
                                                    </span>
                                                    <span className="update-type-hint">
                                                        {intl.formatMessage(updateTypeDescriptions[type])}
                                                    </span>
                                                    {unavailable && (
                                                        <span className="update-type-unavailable">
                                                            {intl.formatMessage(unavailable)}
                                                        </span>
                                                    )}
                                                </>
                                            }
                                        />
                                    );
                                })}
                                {meta.touched && meta.error &&
                                    <div className="invalid-feedback d-block">{meta.error}</div>}
                            </fieldset>
                        )}
                    </Field>
                </Modal.Body>
                <Modal.Footer>
                    <Button onClick={handleSubmit} variant="outline-secondary" disabled={submitting}>
                        <FormattedMessage {...globalMessages.choose} />
                    </Button>
                    <Button onClick={onClose} variant="link">
                        <FormattedMessage {...globalMessages.cancel} />
                    </Button>
                </Modal.Footer>
            </Form>
        }}
        </FinalForm>
    );
}

export default AipUpdateTypeForm;
